import React, { useState } from 'react';
import { Item } from '@/stores/canvasStore';
import { ExternalLink, Globe, FileText, Sheet, Presentation, FolderOpen, FileQuestion, Link2, Play, Newspaper, Pause, Maximize2, Volume2, VolumeX, Instagram, Youtube, Facebook, Linkedin, Twitter, Music2, Pin, AtSign, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { LoadingImage } from '@/components/ui/loading-image';
import { getLinkUrl } from '@/lib/linkUtils';
import { parseSocialUrl } from '@/lib/socialLinks';
import { decodeHtmlEntities } from '@/lib/htmlEntities';
import { isImageFirstSocial } from '@/lib/socialCard';
import { SocialMediaCard } from './SocialMediaCard';

// Shown when a social link's favicon is missing or fails to load
const PLATFORM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: Instagram,
  youtube: Youtube,
  facebook: Facebook,
  linkedin: Linkedin,
  x: Twitter,
  tiktok: Music2,
  pinterest: Pin,
  threads: AtSign,
  reddit: MessageCircle,
  vimeo: Play,
};

// Extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname.includes('youtube.com')) {
      return urlObj.searchParams.get('v');
    }
    if (urlObj.hostname === 'youtu.be') {
      return urlObj.pathname.slice(1).split('/')[0];
    }
    if (urlObj.hostname.includes('youtube.com') && urlObj.pathname.includes('/embed/')) {
      return urlObj.pathname.split('/embed/')[1]?.split('?')[0] || null;
    }
  } catch {
    // Invalid URL
  }
  return null;
};

const isYouTubeUrl = (url: string): boolean => !!getYouTubeVideoId(url);

interface LinkCardProps {
  item: Item;
  /** Let Instagram/TikTok cards upgrade themselves in the background. Off for synced copies. */
  autoRefresh?: boolean;
}

interface LinkContent {
  /** May be missing on rows written by older sync code (X bookmarks only had `tweet_url`). */
  url?: string;
  /** Present on X bookmark items; used as the link target when `url` is absent. */
  tweet_url?: string;
  title?: string;
  description?: string;
  image?: string | null;
  siteName?: string;
  favicon?: string | null;
  imageAspectRatio?: number;
  contentType?: 'product' | 'article' | 'tweet' | 'instagram_reel' | 'video' | 'website' | 'social_media';
  price?: string;
  screenshot?: string | null;
}

// Helper to detect Google Drive link type with icons and colors
const getGoogleDriveInfo = (url: string): { 
  type: string; 
  icon: React.ComponentType<{ className?: string }>;
  color: string;
} | null => {
  try {
    const urlObj = new URL(url);
    const host = urlObj.hostname.toLowerCase();
    
    if (host.includes('docs.google.com')) {
      if (url.includes('/document/')) {
        return { type: 'Google Document', icon: FileText, color: 'text-blue-500' };
      }
      if (url.includes('/spreadsheets/')) {
        return { type: 'Google Spreadsheet', icon: Sheet, color: 'text-green-500' };
      }
      if (url.includes('/presentation/')) {
        return { type: 'Google Slides', icon: Presentation, color: 'text-yellow-500' };
      }
      if (url.includes('/forms/')) {
        return { type: 'Google Form', icon: FileQuestion, color: 'text-purple-500' };
      }
    }
    if (host.includes('drive.google.com')) {
      if (url.includes('/folders/')) {
        return { type: 'Google Drive Folder', icon: FolderOpen, color: 'text-muted-foreground' };
      }
      return { type: 'Google Drive File', icon: FileText, color: 'text-blue-400' };
    }
  } catch {
    // Invalid URL
  }
  return null;
};

// Get display title with fallbacks
const getDisplayTitle = (content: LinkContent): string => {
  // Older rows were saved with entities intact (`&quot;`)
  const title = decodeHtmlEntities(content.title);
  if (title && 
      !title.toLowerCase().includes('sign in') &&
      !title.toLowerCase().includes('login') &&
      !title.toLowerCase().includes('google accounts') &&
      title !== 'Google' &&
      title !== 'Google Docs' &&
      title !== 'Google Sheets' &&
      title !== 'Google Slides') {
    return title;
  }
  
  const url = getLinkUrl(content);

  const driveInfo = getGoogleDriveInfo(url);
  if (driveInfo) {
    return driveInfo.type;
  }
  
  try {
    return new URL(url).hostname;
  } catch {
    // `url` is always a string here, so the fallback itself can never throw.
    if (!url) return 'Link';
    return url.slice(0, 40) + (url.length > 40 ? '…' : '');
  }
};

// Get hostname from URL
const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname.replace('www.', '').toUpperCase();
  } catch {
    return 'LINK';
  }
};

export function LinkCard({ item, autoRefresh = true }: LinkCardProps) {
  const raw = (item.content ?? {}) as unknown as LinkContent;
  const content: LinkContent = raw.description ? { ...raw, description: decodeHtmlEntities(raw.description) } : raw;
  const [imageError, setImageError] = useState(false);
  const [faviconError, setFaviconError] = useState(false);
  const [screenshotError, setScreenshotError] = useState(false);
  
  const url = getLinkUrl(content);
  const driveInfo = getGoogleDriveInfo(url);
  const displayTitle = getDisplayTitle(content);
  const hostname = getHostname(url);
  const contentType = content.contentType || 'website';
  const FallbackIcon = PLATFORM_ICONS[parseSocialUrl(url)?.platform ?? ''] ?? Globe;
  
  // Instagram and TikTok: image-first card (real media, gradient bar, play button)
  if (isImageFirstSocial(content)) {
    return <SocialMediaCard item={item} autoRefresh={autoRefresh} />;
  }

  const handleOpenLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Product Layout: Clean product photo, price top-right, domain bottom-right (mymind style)
  if (contentType === 'product') {
    const hasProductImage = content.image && !imageError;
    const hasProductScreenshot = content.screenshot && !screenshotError;
    const productVisual = hasProductImage ? content.image : (hasProductScreenshot ? content.screenshot : null);

    if (productVisual) {
      return (
        <div
          className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden relative border border-border/40 hover:border-primary/50 transition-colors group cursor-pointer"
          onClick={handleOpenLink}
        >
          {/* Product image fills entire card */}
          <LoadingImage
            src={productVisual}
            alt={displayTitle}
            wrapperClassName="w-full h-full"
            className={cn("w-full h-full", hasProductImage ? "object-contain p-4" : "object-cover object-top")}
            onError={() => hasProductImage ? setImageError(true) : setScreenshotError(true)}
          />

          {/* Price badge - top right */}
          {content.price && (
            <div className="absolute top-3 right-3 bg-card/95 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-sm">
              <span className="text-sm font-bold text-foreground">{content.price}</span>
            </div>
          )}

          {/* Domain badge - bottom right */}
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-card/95 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm">
            <ExternalLink className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground tracking-wide">
              {hostname}
            </span>
          </div>
        </div>
      );
    }

    // Product without image — show title, price, and domain
    return (
      <div
        className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden flex flex-col justify-between border border-border/40 hover:border-primary/50 transition-colors group cursor-pointer p-4"
        onClick={handleOpenLink}
      >
        <div>
          {content.favicon && !faviconError ? (
            <img
              src={content.favicon}
              alt=""
              className="w-7 h-7 rounded-md mb-3"
              onError={() => setFaviconError(true)}
            />
          ) : (
            <FallbackIcon className="w-7 h-7 text-muted-foreground mb-3" />
          )}

          <h3 className="font-bold text-foreground text-base leading-tight line-clamp-3">
            {displayTitle}
          </h3>

          {content.price && (
            <p className="text-primary font-bold text-lg mt-2">{content.price}</p>
          )}

          {content.description && (
            <p className="text-muted-foreground text-sm line-clamp-2 mt-2">
              {content.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 mt-3">
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground tracking-wide">
            {hostname}
          </span>
        </div>
      </div>
    );
  }

  // Article Layout: Favicon icon top-left, large title, hero image below (mymind style)
  // Prefer screenshot over OG image when available (screenshot shows actual page like mymind)
  if (contentType === 'article') {
    const hasScreenshot = content.screenshot && !screenshotError;
    const hasImage = content.image && !imageError;
    const heroImage = hasScreenshot ? content.screenshot! : (hasImage ? content.image! : null);

    return (
      <div
        className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden flex flex-col border border-border/40 hover:border-primary/50 transition-colors group cursor-pointer"
        onClick={handleOpenLink}
      >
        {/* Header: favicon icon + title */}
        <div className="p-4 flex-shrink-0">
          <div className="mb-3">
            {content.favicon && !faviconError ? (
              <img
                src={content.favicon}
                alt=""
                className="w-7 h-7 rounded-md"
                onError={() => setFaviconError(true)}
              />
            ) : (
              <Newspaper className="w-7 h-7 text-muted-foreground" />
            )}
          </div>

          <h3 className="font-bold text-foreground text-lg leading-tight line-clamp-3">
            {displayTitle}
          </h3>
        </div>

        {/* Hero image fills remaining space edge-to-edge */}
        {heroImage && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <LoadingImage
              src={heroImage}
              alt=""
              wrapperClassName="w-full h-full"
              className="w-full h-full object-cover object-top"
              onError={() => hasScreenshot ? setScreenshotError(true) : setImageError(true)}
            />
          </div>
        )}

        {!heroImage && content.description && (
          <div className="flex-1 px-4 pb-4">
            <p className="text-muted-foreground text-sm line-clamp-4">
              {content.description}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Instagram Reel Layout: Video-focused with play indicator
  if (contentType === 'instagram_reel') {
    const hasImage = content.image && !imageError;
    
    return (
      <div 
        className="w-full h-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-lg card-shadow overflow-hidden flex flex-col group cursor-pointer"
        onClick={handleOpenLink}
      >
        {/* Reel image/thumbnail */}
        <div className="relative flex-1 bg-black overflow-hidden min-h-0">
          {hasImage ? (
            <LoadingImage
              src={content.image!} 
              wrapperClassName="w-full h-full"
              alt={displayTitle}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Play className="w-12 h-12 text-white/80" />
            </div>
          )}
          
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-14 h-14 bg-card/90 rounded-full flex items-center justify-center shadow-lg">
              <Play className="w-7 h-7 text-foreground ml-1" fill="currentColor" />
            </div>
          </div>
          
          {/* Instagram badge */}
          <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full">
            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
            </svg>
            <span className="text-xs text-white font-medium">Reel</span>
          </div>
        </div>
        
        {/* Minimal footer */}
        <div className="flex items-center justify-between px-3 py-2 bg-black/50 backdrop-blur-sm">
          <span className="text-xs font-medium text-white/80 truncate flex-1">
            {displayTitle || 'Instagram Reel'}
          </span>
          <ExternalLink className="w-3.5 h-3.5 text-white/60 ml-2" />
        </div>
      </div>
    );
  }

  // YouTube / Video Link Layout: Video-card-style embed with inline playback
  const youtubeVideoId = getYouTubeVideoId(url);
  if ((contentType === 'video' || youtubeVideoId) && youtubeVideoId) {
    return <YouTubeEmbed videoId={youtubeVideoId} title={displayTitle} url={url} thumbnail={content.image || undefined} />;
  }

  // Website/Portfolio Layout: Full-bleed screenshot, floating domain badge (mymind style)
  if (contentType === 'website' && content.screenshot && !screenshotError) {
    return (
      <div
        className="w-full h-full bg-muted rounded-lg card-shadow overflow-hidden relative border border-border/40 hover:border-primary/50 transition-colors group cursor-pointer"
        onClick={handleOpenLink}
      >
        {/* Screenshot fills entire card edge-to-edge */}
        <LoadingImage
          src={content.screenshot}
          wrapperClassName="w-full h-full"
          alt={displayTitle}
          className="w-full h-full object-cover object-top"
          onError={() => setScreenshotError(true)}
        />

        {/* Subtle gradient at bottom for badge legibility */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

        {/* Domain badge - bottom right, floating overlay */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-card/95 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm">
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground tracking-wide">
            {hostname}
          </span>
        </div>
      </div>
    );
  }

  // Tweet Layout
  if (contentType === 'tweet') {
    const hasImage = content.image && !imageError;
    
    return (
      <div 
        className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden flex flex-col border border-border hover:border-primary/50 transition-colors group cursor-pointer"
        onClick={handleOpenLink}
      >
        {/* Twitter header */}
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <svg className="w-5 h-5 text-foreground" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          <span className="text-sm font-medium text-muted-foreground">Post</span>
        </div>
        
        {/* Tweet content */}
        <div className="flex-1 px-3 pb-2 min-h-0 overflow-hidden">
          <h3 className="font-medium text-foreground text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
            {displayTitle}
          </h3>
          {content.description && (
            <p className="text-muted-foreground text-xs line-clamp-3">
              {content.description}
            </p>
          )}
        </div>
        
        {/* Tweet image if available */}
        {hasImage && (
          <div className="h-24 overflow-hidden border-t border-border/50">
            <LoadingImage
              src={content.image!} 
              wrapperClassName="w-full h-full"
              alt=""
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-t border-border/50">
          <span className="text-xs text-muted-foreground">x.com</span>
          <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    );
  }

  // Google Drive Layout
  if (driveInfo) {
    const DriveIcon = driveInfo.icon;
    return (
      <div 
        className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden flex flex-col border border-border hover:border-primary/50 transition-colors group cursor-pointer"
        onClick={handleOpenLink}
      >
        {/* Drive icon header */}
        <div className="flex-1 bg-muted flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <DriveIcon className={`w-12 h-12 ${driveInfo.color}`} />
            <span className="text-xs text-muted-foreground font-medium">{driveInfo.type}</span>
          </div>
        </div>
        
        {/* Title and footer */}
        <div className="p-3 border-t border-border/50">
          <h3 className="font-semibold text-foreground text-sm line-clamp-2 mb-2 group-hover:text-primary transition-colors">
            {displayTitle}
          </h3>
          <div className="flex items-center gap-1.5">
            <img 
              src="https://ssl.gstatic.com/docs/doclist/images/drive_2022q3_32dp.png" 
              alt="Google Drive" 
              className="w-3.5 h-3.5"
            />
            <span className="text-xs text-muted-foreground">Google Drive</span>
            <ExternalLink className="w-3 h-3 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </div>
    );
  }

  // Default/Fallback Layout — show title + image like article cards
  // Prefer screenshot over OG image (OG images are often just logos, screenshots show the real page)
  const hasScreenshot = content.screenshot && !screenshotError;
  const hasImage = content.image && !imageError;
  const displayImage = hasScreenshot ? content.screenshot : (hasImage ? content.image : null);

  if (displayImage) {
    // Image fills the entire card, floating domain badge bottom-right
    return (
      <div
        className="w-full h-full bg-muted rounded-lg card-shadow overflow-hidden relative border border-border/40 hover:border-primary/50 transition-colors group cursor-pointer"
        onClick={handleOpenLink}
      >
        <LoadingImage
          src={displayImage!}
          wrapperClassName="w-full h-full"
          alt={displayTitle}
          className="w-full h-full object-cover object-top"
          onError={() => hasScreenshot ? setScreenshotError(true) : setImageError(true)}
        />

        {/* Gradient for badge legibility */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

        {/* Domain badge - bottom right */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-card/95 backdrop-blur-sm px-2.5 py-1.5 rounded-md shadow-sm">
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground tracking-wide">
            {hostname}
          </span>
        </div>
      </div>
    );
  }

  // Text-only fallback (no image at all) — minimal card with just title + domain
  return (
    <div
      className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden flex flex-col justify-between border border-border/40 hover:border-primary/50 transition-colors group cursor-pointer p-4"
      onClick={handleOpenLink}
    >
      <div>
        {content.favicon && !faviconError ? (
          <img
            src={content.favicon}
            alt=""
            className="w-7 h-7 rounded-md mb-3"
            onError={() => setFaviconError(true)}
          />
        ) : (
          <FallbackIcon className="w-7 h-7 text-muted-foreground mb-3" />
        )}

        <h3 className="font-bold text-foreground text-base leading-tight line-clamp-3">
          {displayTitle}
        </h3>
      </div>

      <div className="flex items-center gap-1.5 mt-3">
        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground tracking-wide">
          {hostname}
        </span>
      </div>
    </div>
  );
}

// ── YouTube embed sub-component (VideoCard-style) ──────────────────────

function YouTubeEmbed({ videoId, title, url, thumbnail }: { videoId: string; title: string; url: string; thumbnail?: string }) {
  const [isPlayingInline, setIsPlayingInline] = useState(false);
  const [isExpandedModal, setIsExpandedModal] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const thumbUrl = thumbnail || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

  const handlePlayInline = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsPlayingInline(!isPlayingInline);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsPlayingInline(false);
    setIsExpandedModal(true);
  };

  const handleOpenExternal = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div
        className="w-full h-full rounded-lg overflow-hidden card-shadow bg-card relative group"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => !isPlayingInline && setShowControls(false)}
        onMouseMove={() => setShowControls(true)}
        title=""
      >
        {/* Inline iframe embed (shown when playing) */}
        {isPlayingInline && (
          <iframe
            src={embedUrl}
            className="absolute inset-0 w-full h-full border-0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            title={title}
            onMouseDown={(e) => e.stopPropagation()}
          />
        )}

        {/* Thumbnail (shown when not playing) */}
        {!isPlayingInline && (
          <LoadingImage
            src={thumbUrl}
            wrapperClassName="w-full h-full"
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
            title=""
          />
        )}

        {/* Play/Pause button overlay */}
        {!isPlayingInline && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors cursor-pointer"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handlePlayInline}
            title=""
          >
            <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
              <Play className="w-7 h-7 text-slate-900 ml-1" fill="currentColor" />
            </div>
          </div>
        )}

        {/* Top-right controls: Expand + External */}
        <div
          className={cn(
            "absolute top-2 right-2 flex gap-1.5 transition-opacity z-10",
            (showControls || !isPlayingInline) ? "opacity-100" : "opacity-0"
          )}
        >
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleOpenExternal}
            className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
            title=""
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleExpandClick}
            className="w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
            title=""
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* YouTube badge + title (when not playing) */}
        {!isPlayingInline && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
            <div className="flex items-center gap-2 text-white text-xs">
              {/* YouTube icon */}
              <svg className="w-4 h-3 flex-shrink-0" viewBox="0 0 28 20" fill="currentColor">
                <path d="M27.4 3.1s-.3-1.9-1.1-2.8C25.1-.9 23.7-.9 23-.9 19.2-1.2 14-1.2 14-1.2h0s-5.2 0-9 .3c-.7.1-2.1.1-3.3 1.2C.9 1.2.6 3.1.6 3.1S.3 5.3.3 7.6v2.1c0 2.2.3 4.5.3 4.5s.3 1.9 1.1 2.8c1.2 1.2 2.6 1.1 3.3 1.3C7.4 18.5 14 18.6 14 18.6s5.2 0 9-.3c.7-.1 2.1-.1 3.3-1.2.8-.9 1.1-2.8 1.1-2.8s.3-2.2.3-4.5V7.6c0-2.2-.3-4.5-.3-4.5zM11.1 13V5.5l6.2 3.8-6.2 3.7z" />
              </svg>
              <span className="truncate flex-1" title="">{title}</span>
            </div>
          </div>
        )}
      </div>

      {/* Expanded YouTube player modal */}
      <Dialog open={isExpandedModal} onOpenChange={setIsExpandedModal}>
        <DialogContent
          className="max-w-4xl p-0 bg-black border-none overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <DialogTitle className="sr-only">Playing: {title}</DialogTitle>
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={embedUrl}
              className="absolute inset-0 w-full h-full border-0"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              title={title}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
